import React, { useState, useEffect, useRef, useCallback } from 'react';
import { bool, func, object } from 'prop-types';
import { noop, throttle } from 'lodash';
import classNames from 'classnames';
import { I18n } from '@fiverr-private/i18n-react';
import { logger } from '@fiverr-private/obs';
import Image from '../../Image';
import CloudinaryPdf from '../../CloudinaryPdf';
import PinchToZoom from '../../PinchToZoom';

const PdfSlide = ({
    isActive = false,
    expanded = false,
    preload = false,
    slide,
    onZoomActive = noop,
    onZoomIdle = noop,
    onClick = noop,
    reportAction = noop,
}) => {
    const pageUrls = useRef([]);
    const wrapperNode = useRef();
    const [isSinglePage, setIsSinglePage] = useState(false);
    const [isLoadingMorePages, setIsLoadingMorePages] = useState(true);
    const [isScrollable, setIsScrollable] = useState(false);
    const [hasBeenScrolled, setHasBeenScrolled] = useState(false);

    const updateScrollStatus = useCallback(() => {
        if (wrapperNode.current && !hasBeenScrolled) {
            const { scrollHeight, offsetHeight, scrollTop } = wrapperNode.current;
            setIsScrollable(scrollHeight > offsetHeight);
            setHasBeenScrolled(scrollTop > 5);
        }
    }, [hasBeenScrolled]);

    const throttledUpdateScrollStatus = useCallback(() => {
        throttle(updateScrollStatus, 250);
    }, [updateScrollStatus]);

    const handlePageLoaded = useCallback(
        (page) => {
            pageUrls.current[page.index] = page.src;
            throttledUpdateScrollStatus();
        },
        [throttledUpdateScrollStatus]
    );

    useEffect(() => {
        if ((isActive || preload) && expanded && pageUrls.current.length === 0) {
            const { media } = slide;
            const { res, path, assetId } = CloudinaryPdf.getPartsFromUrl(media.pages[0]);

            const fallbackToPreview = () => {
                pageUrls.current = media.pages || [media.medium];
                setIsSinglePage(true);
                setIsLoadingMorePages(false);
            };

            if (!assetId) {
                fallbackToPreview();
            } else {
                setIsLoadingMorePages(true);

                const cloudinary = new CloudinaryPdf(res);
                cloudinary
                    .getPages({
                        path,
                        assetId,
                        width: 1260,
                        batchSize: 6,
                        onPageLoaded: handlePageLoaded,
                    })
                    .then((pages) => {
                        setIsLoadingMorePages(false);
                        setIsSinglePage(pages.length === 1);
                    })
                    .catch((error) => {
                        logger.error(`assets sharing - Failed to load first pdf batch, error: ${error}`);
                        fallbackToPreview();
                    });
            }
        }
    }, [slide, isActive, preload, expanded, handlePageLoaded]);

    const onClickWrapper = () => {
        onClick();
        !expanded && reportAction();
    };

    const renderPages = () => {
        const lazyLoad = !isActive && !preload;
        const classes = classNames('slide-pages', { 'single-page': isSinglePage, 'loading-more': isLoadingMorePages });

        const pages =
            pageUrls.current.length > 0
                ? pageUrls.current.map((page, index) => (
                      <li key={`${index}--${page}`}>
                          <Image lazyLoad={lazyLoad} isActive={isActive} alt="" src={page} />
                      </li>
                  ))
                : null;

        const loadingMore = isLoadingMorePages ? <li key="loading-more" className="loading-more" /> : null;

        return pages || loadingMore ? (
            <div className={classes}>
                <PinchToZoom
                    rotate={false}
                    minScale={1.5}
                    onActive={onZoomActive}
                    onIdle={onZoomIdle}
                    preventSingleTouchEvents={false}
                >
                    <ul ref={wrapperNode} onScroll={throttledUpdateScrollStatus}>
                        {pages}
                        {loadingMore}
                    </ul>
                </PinchToZoom>
            </div>
        ) : null;
    };

    const renderThumbnail = () => {
        const { name, media } = slide;
        const lazyLoad = !isActive && !preload;

        return (
            <figure className="thumbnail" tabIndex="0">
                <Image lazyLoad={lazyLoad} isActive={isActive} alt={name} src={media.medium} />
            </figure>
        );
    };

    const slideClasses = classNames('slide', 'slide-pdf', {
        'scroll-hint': isScrollable && !hasBeenScrolled,
    });

    return (
        <div className={slideClasses} onClick={onClickWrapper}>
            {expanded ? renderPages() : renderThumbnail()}
            {!expanded && (
                <div className="slide-fullscreen">
                    <I18n k="assets_sharing.gallery.view_presentation" />
                </div>
            )}
        </div>
    );
};

PdfSlide.propTypes = {
    isActive: bool,
    expanded: bool,
    preload: bool,
    slide: object,
    onZoomActive: func,
    onZoomIdle: func,
    onClick: func,
    reportAction: func,
};

export default PdfSlide;
